import * as _ from 'lodash';
import * as React from 'react';
import { css } from 'aphrodite';
import history from 'login-utils/history';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';

import * as onboardingForm from 'login-utils/onboardingForm';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import AutoPurchaseSVG from 'login-components/Common/svg/AutoPurchaseSVG';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import ConstructionSVG from 'login-components/Common/svg/ConstructionSVG';
import HeloanSVG from 'login-components/Common/svg/HeloanSVG';
import HelocSVG from 'login-components/Common/svg/HelocSVG';
import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import MortgageSVG from 'login-components/Common/svg/MortgageSVG';
import PersonalSVG from 'login-components/Common/svg/PersonalSVG';
import CreditCardSVG from 'login-components/Common/svg/CreditCardSVG';
import SpecialtyVehicleSVG from 'login-components/Common/svg/SpecialtyVehicleSVG';
import States from 'login-constants/states';
import { addNewLoanToBorrower, shouldAddNewLoanToBorrower } from 'login-utils/createLoanUtils';
import { LargeIconButtonConfig } from 'login-components/Common/Buttons/LargeIconButton';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';

export interface LoanTypeButtonConfig extends LargeIconButtonConfig {
  value: LoanType | LoanTypeCategory;
}

export const loanTypeChooserConfigs: LoanTypeButtonConfig[] = [
  {
    value: 'MORTGAGE',
    iconComponent: MortgageSVG,
    buttonId: 'it-chooseLoanTypeMortgage',
    text: 'Mortgage',
    subText: 'Buy a new home or refinance your current one.',
  },
  {
    value: 'HELOAN',
    iconComponent: HeloanSVG,
    buttonId: 'it-chooseLoanTypeHELOAN',
    text: 'Home equity loan',
    subText: 'Borrow a lump sum and pay it back over a fixed term.',
  },
  {
    value: 'HELOC',
    iconComponent: HelocSVG,
    buttonId: 'it-chooseLoanTypeHELOC',
    text: 'Home equity line of credit',
    subText: 'Tap into the equity in your home.',
  },
  {
    value: 'CONSTRUCTION',
    iconComponent: ConstructionSVG,
    buttonId: 'it-chooseLoanTypeConstruction',
    text: 'Construction',
    subText: 'Build a new home or make renovations to an existing home.',
  },
  {
    value: 'AUTO',
    iconComponent: AutoPurchaseSVG,
    buttonId: 'it-chooseLoanTypeAuto',
    text: 'Auto loan',
    subText: 'Buy a car or change your loan terms.',
  },
  {
    value: 'PERSONAL',
    iconComponent: PersonalSVG,
    buttonId: 'it-chooseLoanTypePersonal',
    text: 'Personal loan',
    subText: 'Borrow a lump sum in the form of loan, or as needed through a line of credit.',
  },
  {
    value: 'SPECIALTY_VEHICLE',
    iconComponent: SpecialtyVehicleSVG,
    buttonId: 'it-chooseLoanTypeSpecialtyVehicle',
    text: 'Specialty vehicle',
    subText: 'Buy or refinance a specialty vehicle, like a motorcycle, boat, or recreational vehicle.',
  },
  {
    value: 'CREDIT_CARD',
    iconComponent: CreditCardSVG,
    buttonId: 'it-chooseLoanTypeCreditCard',
    text: 'Credit card',
    subText: 'Apply for a new credit card.',
  },
];

export type ChooseLoanTypeProps = ReactRouter.RouteComponentProps<{}, {}>;

interface ChooseLoanTypeState {
  isSubmitting: boolean;
  error?: string;
}

export class ChooseLoanType extends React.Component<
  ChooseLoanTypeProps & FormProps<DataShape, ChooseLoanTypeProps, ReduxState>,
  ChooseLoanTypeState
> {
  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
    };
  }

  getLoanTypeConfigs = () => {
    const supportedLoanTypesAndCategories = onboardingRoutingUtils.getSupportedLoanTypesAndCategories();

    return loanTypeChooserConfigs.filter(({ value }) => _.includes(supportedLoanTypesAndCategories, value));
  };

  onSelect = async loanType => {
    // these loan types must continue through the normal signup flow to fill out additional signup parameters
    const hasAdditionalProductSelection = onboardingRoutingUtils.loanTypeRequiresAdditionalSignupParameters(loanType);
    if (!hasAdditionalProductSelection && shouldAddNewLoanToBorrower()) {
      this.setState({
        error: '',
        isSubmitting: true,
      });
      // Directly create a new loan for the user if they are logged in
      try {
        await addNewLoanToBorrower({ loanType });
      } catch (err) {
        this.setState({
          error: 'Failed to create application, please try again',
          // Only unset on error, since we navigate away in the success case
          // and don't want the user to be able to submit again
          isSubmitting: false,
        });
      }
    } else {
      // Otherwise, proceed to signup via the borrower onboarding flow
      const { location } = this.props;
      const search = location.search || /* istanbul ignore next: (UI-5482) */ '';
      history.push(`${States.SIGNUP}${search}`);
    }
  };

  render() {
    return (
      <div>
        {this.state.isSubmitting ? (
          <div className={css(paneFormStyles.loadingSpinnerWrapper)}>
            <LoadingSpinner />
          </div>
        ) : (
          <React.Fragment>
            {this.state.error && (
              <div className={css(paneFormStyles.errorWrapper)}>
                <ErrorMessage errorText={this.state.error} />
              </div>
            )}
            <Field
              name={onboardingForm.FIELDS.loanType}
              configs={this.getLoanTypeConfigs()}
              onSelect={this.onSelect}
              component={LargeIconButtonGroup}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default reduxForm<FormProps<DataShape, ChooseLoanTypeProps, ReduxState>, ChooseLoanTypeProps, ReduxState>(
  onboardingForm.FORM_CONFIG,
)(ChooseLoanType);
